import { graphql } from 'react-relay';
import type { FC, MouseEvent, KeyboardEvent, ReactElement } from 'react';
import React, { useCallback, Fragment } from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { defineMessages, injectIntl } from 'react-intl';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { ShortcutEvent } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { fg } from '@confluence/feature-gating';

import { ActionErrorFlag } from '../SharedComponents.tsx';
import type { AnalyticsOptions } from '../SpaceStar/SpaceStar.tsx';

import type {
	PageStarActionButtonPageUnstarMutation as PageStarActionButtonPageUnstarMutation$data,
	PageStarActionButtonPageUnstarMutationVariables as PageStarActionButtonPageUnstarMutation$variables,
} from './__types__/PageStarActionButtonPageUnstarMutation.ts';
import type {
	PageStarActionButtonPageStarMutation as PageStarActionButtonPageStarMutation$data,
	PageStarActionButtonPageStarMutationVariables as PageStarActionButtonPageStarMutation$variables,
} from './__types__/PageStarActionButtonPageStarMutation.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
const PageStarActionButtonPageStarMutation = graphql`
	mutation PageStarActionButtonPageStarMutation($id: ID!) {
		favouritePage(favouritePageInput: { pageId: $id }) {
			content {
				id
				metadata {
					currentuser {
						favourited {
							isFavourite
							favouritedDate
						}
					}
				}
			}
		}
	}
`;
const PageStarActionButtonPageUnstarMutation = graphql`
	mutation PageStarActionButtonPageUnstarMutation($id: ID!) {
		unfavouritePage(favouritePageInput: { pageId: $id }) {
			content {
				id
				metadata {
					currentuser {
						favourited {
							isFavourite
							favouritedDate
						}
					}
				}
			}
		}
	}
`;
type IconSpacing = 'none' | 'spacious';

export type PageStarProps = {
	contentId: string;
	isStarred: boolean;
	onStar?: (e: MouseEvent | KeyboardEvent | ShortcutEvent) => void;
	onUnstar?: (e: MouseEvent | KeyboardEvent | ShortcutEvent) => void;
	analytics?: AnalyticsOptions;
	spacing?: IconSpacing;
	isDisabled?: boolean;
	contentTitle?: string;
	shouldShowFlags?: boolean;
};

type ButtonChildrenProps = {
	toggle(e: MouseEvent | KeyboardEvent | ShortcutEvent): void;
};

export type ButtonChildren = {
	children(props: ButtonChildrenProps): ReactElement;
};

const i18n = defineMessages({
	starErrorTitle: {
		id: 'action-buttons.content.star.error.title',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: "We're having trouble starring this",
		description:
			"Title of error flag shown when a user attempts to click the 'Star' button but the starring action fails",
	},
	unstarErrorTitle: {
		id: 'action-buttons.content.unstar.error.title',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: "We're having trouble unstarring this",
		description:
			"Title of error flag shown when a user attempts to click the 'Unstar' button but the unstarring action fails",
	},
	starSuccessFlagTitle: {
		id: 'action-buttons.content.star.success.title',
		defaultMessage: '{contentTitle} starred',
		description:
			"Title of success flag shown when a user clicks the 'Star' button and the action is successful",
	},
	unstarSuccessFlagTitle: {
		id: 'action-buttons.content.unstar.success.title',
		defaultMessage: '{contentTitle} unstarred',
		description:
			"Title of success flag shown when a user clicks the 'Unstar' button and the action is successful",
	},
	errorDescription: {
		id: 'action-buttons.error.description',
		defaultMessage: 'Refresh the page and try again.',
		description:
			'Body of error flag shown when user attempts an action (e.g. star) but it fails. Shown below the error title.',
	},
});

const useStarAnalytics = (contentId, analytics?: AnalyticsOptions) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const stringAttrs = JSON.stringify(analytics?.attributes);
	return useCallback(
		(action: 'created' | 'deleted') => {
			if (!analytics) {
				return;
			}
			const { source, containerId, attributes } = analytics;
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action,
					actionSubject: 'favouritePage',
					source,
					attributes,
					objectId: contentId,
					...(containerId && { containerId }),
				},
			}).fire();
		},
		// Using stringAttrs as dependency instead of analytics.attributes
		// https://github.com/facebook/react/issues/14476
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[createAnalyticsEvent, analytics?.source, stringAttrs],
	);
};

const PageStarComponent: FC<
	PageStarProps & ButtonChildren & WrappedComponentProps & WithFlagsProps
> = ({
	contentId,
	isStarred,
	onStar,
	onUnstar,
	analytics,
	intl,
	children,
	flags,
	contentTitle = '',
	shouldShowFlags = false,
}) => {
	const sendAnalyticsEvent = useStarAnalytics(contentId, analytics);
	const [star, { error: starError }] = useMutationInterop<
		PageStarActionButtonPageStarMutation$data,
		PageStarActionButtonPageStarMutation$variables
	>(PageStarActionButtonPageStarMutation, {
		variables: { id: contentId },
		onCompleted: () => {
			sendAnalyticsEvent('created');
		},
		optimisticResponse: {
			favouritePage: {
				__typename: 'FavouritePagePayload',
				content: {
					__typename: 'Content',
					id: contentId,
					metadata: {
						__typename: 'ContentMetadata',
						currentuser: {
							__typename: 'ContentMetadata_CurrentUserMetadataProvider_currentuser',
							favourited: {
								__typename: 'FavouritedSummary',
								isFavourite: true,
								favouritedDate: new Date().toISOString(),
							},
						},
					},
				},
			},
		},
	});
	const [unstar, { error: unstarError }] = useMutationInterop<
		PageStarActionButtonPageUnstarMutation$data,
		PageStarActionButtonPageUnstarMutation$variables
	>(PageStarActionButtonPageUnstarMutation, {
		variables: { id: contentId },
		onCompleted: () => {
			sendAnalyticsEvent('deleted');
		},
		optimisticResponse: {
			unfavouritePage: {
				__typename: 'FavouritePagePayload',
				content: {
					__typename: 'Content',
					id: contentId,
					metadata: {
						__typename: 'ContentMetadata',
						currentuser: {
							__typename: 'ContentMetadata_CurrentUserMetadataProvider_currentuser',
							favourited: null,
						},
					},
				},
			},
		},
	});
	const toggleStar = useCallback(
		(e: MouseEvent | KeyboardEvent | ShortcutEvent) => {
			e.preventDefault();
			if (isStarred) {
				onUnstar && onUnstar(e);
				unstar()
					.then(() => {
						if (shouldShowFlags && fg('confluence_frontend_content_wrapper')) {
							void flags.showSuccessFlag({
								title: intl.formatMessage(i18n.unstarSuccessFlagTitle, { contentTitle }),
								isAutoDismiss: true,
							});
						}
					})
					.catch((_) => {});
			} else {
				onStar && onStar(e);
				star()
					.then(() => {
						if (shouldShowFlags && fg('confluence_frontend_content_wrapper')) {
							void flags.showSuccessFlag({
								title: intl.formatMessage(i18n.starSuccessFlagTitle, { contentTitle }),
								isAutoDismiss: true,
							});
						}
					})
					.catch((_) => {});
			}
		},
		[isStarred, onStar, star, onUnstar, unstar, contentTitle, flags, intl, shouldShowFlags],
	);

	return (
		<Fragment>
			{children({
				toggle: toggleStar,
			})}
			{starError && (
				<ActionErrorFlag
					title={intl.formatMessage(i18n.starErrorTitle)}
					description={intl.formatMessage(i18n.errorDescription)}
					error={starError}
				/>
			)}
			{unstarError && (
				<ActionErrorFlag
					title={intl.formatMessage(i18n.unstarErrorTitle)}
					description={intl.formatMessage(i18n.errorDescription)}
					error={unstarError}
				/>
			)}
		</Fragment>
	);
};

export const PageStar = withFlags(injectIntl(PageStarComponent));
